import isEmpty from 'lodash/isEmpty';
import { change } from 'redux-form';
import {
    CheckoutLifecycleEventPostMessageName,
    isWebPlatform,
    checkoutPostMessage,
} from '@eventbrite/checkout-postmessage-api';
import { gettext } from '@eventbrite/i18n';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import { transformUserInstruments } from '../../api/transformations/userInstruments';
import { getAvailablePaymentMethods as getAvailablePaymentMethodsApi } from '../../api/payIn';
import { getVaultInstruments as getVaultInstrumentsApi } from '../../api/users';

import {
    CHECKOUT_FORM_NAME,
    INSTRUMENT_TYPES,
    PAYMENT_METHODS,
} from '../../constants';

import { isUserLoggedIn } from '../../selectors/user';
import { getAvailablePaymentMethods } from '../../selectors/payIn';
import { showPayInConfirmation as showPayInConfirmationAction } from '../payInConfirmation';
import {
    getUserEmail,
    getUserFirstName,
    getUserLastName,
} from '../../selectors/user';
import { changeSurveyField } from '../../utils/surveys';
import {
    BUYER_EMAIL_FIELD,
    BUYER_FIRST_NAME_FIELD,
    BUYER_LAST_NAME_FIELD,
} from '../../containers/checkout/constants';
import { handlePayInError } from './utils';
import { getCurrency, getEventId } from '../../selectors/event';
import { isIdentificationRequiredForCurrency } from '../../models/currency';
import { getUpdateUserInstrumentsAction } from '../user';
import { updateAvailablePaymentMethodsAction } from '../order';

/**
 * Internal actions
 */
export const getAndSaveAvailablePaymentMethods =
    () => async (dispatch, getState) => {
        const { user: { publicId: userId } = {} } = getState();

        const constrainedInstrumentType = ''; // TODO: Previously this was taken from tickets.constrainedInstrumentType
        const constrainedPaymentMethods = []; // TODO: Previously this was taken from tickets.constrainedPaymentMethods

        const state = getState();

        const { response: availablePaymentMethodsResponse } =
            await getAvailablePaymentMethodsApi(state);
        let result = await dispatch(
            updateAvailablePaymentMethodsAction(
                availablePaymentMethodsResponse,
            ),
        );

        const availablePaymentMethods = getAvailablePaymentMethods(state);

        const hasOnlyOnePaymentMethodAndInstrumentType =
            availablePaymentMethods.length === 1
                ? availablePaymentMethods[0].instrumentTypes.length === 1
                : false;

        if (hasOnlyOnePaymentMethodAndInstrumentType) {
            result = await dispatch(
                change(
                    CHECKOUT_FORM_NAME,
                    'paymentMethod',
                    availablePaymentMethods[0].instrumentTypes[0].type,
                ),
            );
        }

        // TODO: EB-112961 Currently we can't support vault instruments in currencies requiring identification (like BRL and ARG)
        const isVaultSupportedCurrency =
            !isIdentificationRequiredForCurrency[getCurrency(state)];

        // Fetch and save user's stored CC payment methods if user is logged in
        if (isUserLoggedIn(state) && isVaultSupportedCurrency) {
            const { response: instrumentsResponse } =
                await getVaultInstrumentsApi(userId, new Date());

            const transformed = transformUserInstruments(instrumentsResponse);
            const eppMethods = availablePaymentMethods.find(
                (method) => method.type === 'eventbrite',
            );

            if (eppMethods) {
                let allowedUserInstruments = transformed.userInstruments;
                const creditCardInstrument = eppMethods.instrumentTypes.find(
                    (instrument) => instrument.type === PAYMENT_METHODS.CREDIT,
                );

                // Filter out user instruments that do not fit in with the allowed CC variants.
                // ex) Don't show stored VISA card if the order can only use AMEX cards.
                if (creditCardInstrument) {
                    const allowedCards = creditCardInstrument.variants;

                    allowedUserInstruments = allowedUserInstruments
                        .filter((userInstrument) =>
                            allowedCards.includes(
                                userInstrument.instrument.cardType,
                            ),
                        )
                        .filter(
                            (userInstrument) =>
                                isEmpty(constrainedPaymentMethods) ||
                                constrainedPaymentMethods.includes(
                                    userInstrument.instrument.cardType,
                                ),
                        );
                }

                dispatch(
                    getUpdateUserInstrumentsAction(allowedUserInstruments),
                );

                if (
                    !isEmpty(allowedUserInstruments) ||
                    constrainedInstrumentType === INSTRUMENT_TYPES.CREDIT_CARD
                ) {
                    /**
                     * If there are stored payment methods or constrained instrument type is 'CREDIT_CARD',
                     * payment method selection is pre-set to CREDIT and the first stored payment method in
                     * the list is pre-selected. If there are no stored payment methods, fall back to 'CREDIT',
                     * which corresponds to the "add a new card" option.
                     */
                    const allowedVaultId = allowedUserInstruments.length
                        ? allowedUserInstruments[0].vaultId
                        : PAYMENT_METHODS.CREDIT;

                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'paymentMethod',
                            PAYMENT_METHODS.CREDIT,
                        ),
                    );
                    dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'payment.vaultId',
                            allowedVaultId,
                        ),
                    );
                } else {
                    return dispatch(
                        change(
                            CHECKOUT_FORM_NAME,
                            'payment.vaultId',
                            PAYMENT_METHODS.CREDIT,
                        ),
                    );
                }
            }
        }
        return result;
    };

/**
 * Saves and places a pay-in that will be paid by standard credit card (ex. Visa, Mastercard, etc...).
 * After placing the pay-in, the app will continue to the pay-in confirmation page.
 */
const _getCreditCardOrderPromise = async ({ dispatch, payIn }) => {
    try {
        await payIn.saveAndPlacePromise;
        const platform = checkoutPostMessage({
            messageName:
                CheckoutLifecycleEventPostMessageName.payInStatusPlaced,
        });

        if (isWebPlatform(platform)) {
            await dispatch(showPayInConfirmationAction());
        }
    } catch (error) {
        return Promise.reject(handlePayInError(dispatch, error));
    }
};

const _requestNonceBraintreePayment = ({ payIn, dispatch }) => {
    if (!payIn.formData.payment) {
        return new Promise(() => {
            throw new CustomSubmissionError(
                {
                    _error: gettext(
                        'You must authorize with PayPal before you can submit your order',
                    ),
                },
                {},
            );
        }).catch((error) => handlePayInError(dispatch, error));
    }
    return _getCreditCardOrderPromise({ payIn, dispatch });
};

export const requestPayment =
    (paymentMethod, payIn) => (dispatch, getState) => {
        const paymentActions = {
            [PAYMENT_METHODS.NONCE_BRAINTREE]: _requestNonceBraintreePayment,
        };

        const action =
            paymentActions[paymentMethod] || _getCreditCardOrderPromise;

        return action({ payIn, getState, dispatch }).catch((error) =>
            handlePayInError(dispatch, error),
        );
    };

export const prefillSurveyFields =
    (formName = CHECKOUT_FORM_NAME) =>
    (dispatch, getState) => {
        const state = getState();

        const emailToPrefill = getUserEmail(state);
        if (emailToPrefill) {
            dispatch(
                changeSurveyField(BUYER_EMAIL_FIELD, emailToPrefill, formName),
            );
        }

        const firstNameToPrefill = getUserFirstName(state);
        if (firstNameToPrefill) {
            dispatch(
                changeSurveyField(
                    BUYER_FIRST_NAME_FIELD,
                    firstNameToPrefill,
                    formName,
                ),
            );
        }

        const lastNameToPrefill = getUserLastName(state);
        if (lastNameToPrefill) {
            dispatch(
                changeSurveyField(
                    BUYER_LAST_NAME_FIELD,
                    lastNameToPrefill,
                    formName,
                ),
            );
        }
    };
